import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout } from "@/comn/components"; // 화면 구성 컴포넌트
import { useForm, useFetch, useModal, useStore, useToast, usePopup, useResource, usePage } from "@/comn/hooks"; // hook
import { BASE, URLS, APIS, SF_HS_DTL_SRCH } from "@/tz/go/egaz/osci/ptl/info/hscd/services/OsciHsCdInfoService"; // 서비스

/*
 * @ 화면 컴포넌트 주석
 * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
 * @ !== ... ==! 치환 부호는 개발 완료 후 한글 주석을 인지하여 제거하기 위한 표시
 * @ 기본 여러줄의 주석 형태를 사용
 * @ 포맷
 * @ [화면명 영문]
 * @ [지환부호시작] [화면명 한글] [치환부호종료]
 *
 * 화면명 영문
 * !== 화면명 한글 ==!
 */

/*
 * APC Management List
 * !== APC 상세조회 ==!
 */
export const OSCI0110002Q = (props: any) => {
    /*
     * @ 변수에 대한 주석 (기본 Hook 제외)
     * @ 메타 시스템을 참고 하여 표준 단어와 용어를 사용하여 작성
     * @ 기본 한줄 주석 형태를 사용
     * @ 포맷
     * @ [지환부호시작] [한글변수명] [지환부호종료]
     *
     * 예시) const pgeUid = "CGME0411001Q"; // Page Unique identifier !== 화면 고유 식별자 ==!
     * 선언과 동시에 전개가 이루어 지는 경우 변수의 위쪽에 작성
     */

    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */

    const { pgeUid, menuId } = usePage(); // Page Unique identifier !== 화면 고유 식별자 ==!
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { id } = useParams(); // Key Parameter !== 넘어온 키 Parameter ==!
    const [isSearch, setIsSearch] = useState(false);
    useResource({
        defaultSchema: [{ area: "hsCd" }],
    });

    /*
     * @ 화면에서 사용하는 form 객체를 선언
     * @ 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * @ 검색 조건일 경우 구분을 위해 폼 이름 뒤에 Srch
     * @ const form = {
     *      [form 이름] : useForm({
     *          defaultSchema: [form의 schema 구조],
     *          defaultValues: [form의 기본 값] || {},
     *      }),
     *      ...
     * }
     */

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // HS Detail Select !== HS 상세 조회==!
        hsDtlSrch: useForm({
            defaultSchema: SF_HS_DTL_SRCH,
            defaultValues: {},
        }),
    };

    /*
     * 화면에서 사용하는 api 호출 객체를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * Back-End Controller 메소드명과 동일하게 작성(Back-End 개발 가이드 참조)
     * 
     * 다건 조회    get         getRpckItmAppList
     * 
     * const fetch = {
     *      [fetch 이름] : useFetch({
     *          api: [fetch 실행 api, service 파일의 APIS 에 기재],
     *          enabled: [[fetch 가 실행되기 위한 조건]],
                key: [[fetch 에서 변화를 감지하여 자동실행할 변수]],
                onSuccess : [fetch 실행 성공시 실행],
                onError : [fetch 실행 실패시 실행],
                showToast: [성공/실패 결과 Toast메세지 표시 여부],
     *      }),
     *      ...
     * }
     */

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get HS Detaile Select !== HS 관리 상세 조회 ==!
        getHsDtlSelc: useFetch({
            api: (data) => {
                if (isSearch) {
                    return APIS.getClreHsDtlSelc(form.hsDtlSrch.getValues().hsCdSrch);
                } else {
                    return APIS.getClreHsDtlSelc(id);
                }
            },
            enabled: !!id,
            onSuccess: (data) => {
                form.hsDtlSrch.setValues({
                    ...data.osciHsCdDto.content,
                });
                console.log(data);

                form.hsDtlSrch.setValue("hsCdSrch", data.osciHsCdDto.content.hsCd);
            },
            onError: (error) => {
                console.log(error);
            },
            showToast: true,
        }),
    };

    /*
     * 화면에서 사용하는 이벤트, 함수를 선언
     * 기본 변수명은 메타 단어 조합 카멜 표기법을 따름
     * fetch 를 호출 하는 메소드: fetch 명과 동일하게 작성
     * 버튼이나 그리드의 이벤트의 경우 : [이벤트종류]_[이벤트대상타입]_[이벤트대상명]
     * 이벤트 대상 타입 : Btn , Grid ... 카멜표기법으로 표시, 첫글자 대문자
     * 이벤트 명 : 카멜표기법으로 표시, 첫글자 대문자
     *
     * const handler = {
     *      [handler 이름] : ()=>{ 함수 },
     *      click_Btn_Save : 저장버튼 클릭
     *      click_Grid_RpckItmAppList : APC 관리 그리드 클릭
     *      ...
     * }
     *
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Get APC Detaile Select !== APC 상세조회 조회 ==!
        getHsDtlSelc: () => {
            form.hsDtlSrch.handleSubmit(
                (data) => {
                    setIsSearch(true);
                    fetch.getHsDtlSelc.fetch();
                },
                (data) => {
                    toast.showToast({ type: "warning", content: "msg.osc.00003" });
                },
            )();
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        setIsSearch(true);

        form.hsDtlSrch.setSchemas(["hsCd", "hsDesc", "itmQtyUtCd", "useYn", "regstId"], { edit: false });
    }, []);

    /*
     * @ 라벨 생성(명명 규칙)
     * @ prefix
     * @ 일반라벨 : L_
     * @ 버튼 : B_
     * @ 제목 : T_
     * @ 메세지 : msg.00000 [msg.숫자5자리] , 00000 ~ 10000 까지는 공통영역
     * @ 매세지를 제외한 라벨은 메타 단어, 용어 조합, 메세지는 숫자 5자리 일련번호로 정의
     * @ 대문자로 표시
     * @ 단어와 단어 사이 용어와 용어 사이는 "_" 로 구분
     */
    return (
        <Page>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                {/* APC !== APC ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.hsCdSrch} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>
            <Layout.Right>
                {
                    <Layout.Button
                        role={"search"}
                        onClick={() => {
                            handler.getHsDtlSelc();
                        }}
                    >
                        {t("B_SRCH")}
                    </Layout.Button>
                }
            </Layout.Right>
            <form>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                {/* hsCd !== hsCd ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.hsCd} />
                            </Group.Row>
                            <Group.Row>
                                {/* hsCd !== hsDesc ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.hsDesc} />
                            </Group.Row>
                            <Group.Row>
                                {/* hsCd !== qty ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.itmQtyUtCd} />
                                {/* hsCd !== useYn ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.useYn} />
                            </Group.Row>
                            <Group.Row>
                                {/* hsCd !== register ==!  */}
                                <Group.Control {...form.hsDtlSrch.schema.regstId} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </form>

            <Layout.Left>
                {
                    <Layout.Button
                        role={"list"}
                        onClick={() => {
                            navigate(`${URLS.osci0110001q}`);
                        }}
                    >
                        {t("B_LST")}
                    </Layout.Button>
                }
            </Layout.Left>
        </Page>
    );
};
